/**
 * BulkPriceChangesPage (/bulk-prices) — hauler-wide price changes across many customers at once
 * (gap 1 PR 2, DEC-383, D-13 Raise prices, D-12 the Excel file). Named sections render in BULK_SECTIONS order, each as a
 * <section> with an h2; the page reads the company's today once and hands it to every section.
 *
 * OPEN-213 appends its zone-price / customer-fee section; no second page.
 *
 * The nav entry and route exist only for MANAGE_CUSTOMER_PRICES roles (App.tsx). That is UX
 * only: the API enforces the permission on every preview and apply.
 */

import React from 'react';
import { RaisePricesSection } from '../features/billing/RaisePricesSection';
import { JobTypeExcelSection } from '../features/billing/JobTypeExcelSection';
import { useJobTypeCatalog } from '../features/billing/customer-prices-hooks';

export interface BulkSectionProps {
  /** Company-local 'YYYY-MM-DD'; the earliest start date. */
  readonly today: string;
}

export interface BulkSection {
  readonly id: string;
  readonly title: string;
  readonly render: React.ComponentType<BulkSectionProps>;
}

/** The page's sections, in order. */
export const BULK_SECTIONS: readonly BulkSection[] = [
  { id: 'raise-job-type-prices', title: 'Raise prices', render: RaisePricesSection },
  { id: 'job-type-excel', title: 'Job type prices in Excel', render: JobTypeExcelSection },
];

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

export function BulkPriceChangesPage(): React.JSX.Element {
  const catalog = useJobTypeCatalog();
  const today = catalog.data?.today;

  return (
    <div style={{ padding: '24px', maxWidth: '1100px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '6px' }}>Bulk price changes</h2>
      <p style={{ ...helpStyle, marginBottom: '20px' }}>
        Change prices for many customers at once. Preview every change before you save it.
      </p>
      {catalog.isLoading && (
        <p role="status" style={helpStyle}>
          Loading…
        </p>
      )}
      {catalog.isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Couldn&apos;t load this page. Reload the page to try again.
        </p>
      )}
      {today !== undefined &&
        BULK_SECTIONS.map((s) => {
          const Render = s.render;
          const headingId = `bulk-section-${s.id}`;
          return (
            <section key={s.id} aria-labelledby={headingId} style={{ marginBottom: '28px' }}>
              <h2
                id={headingId}
                style={{ fontSize: '16px', fontWeight: 600, marginBottom: '10px' }}
              >
                {s.title}
              </h2>
              <Render today={today} />
            </section>
          );
        })}
    </div>
  );
}
